import { Box, Chip, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraceDto } from '../../types/Constants'
import PageSpinner from '@src/shared/components/PageSpinner'

const isReadOperation = (operationName: string) =>
  operationName.startsWith('GET ') || operationName.startsWith('GET-') || operationName.includes('Event')

interface TracesPanelProps {
  traces: TraceDto[]
  isLoading: boolean
}

const TracesPanel = ({ traces, isLoading }: TracesPanelProps) => {
  const { t } = useTranslation()
  const [hideReadRequests, setHideReadRequests] = useState(false)

  const filteredTraces = useMemo(
    () => (hideReadRequests ? traces.filter((trace) => !isReadOperation(trace.operationName)) : traces),
    [traces, hideReadRequests],
  )

  if (isLoading) return <PageSpinner isLoading />

  if (traces.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {t('investigation.traces.noTraces')}
      </Typography>
    )
  }

  const columns: GridColDef<TraceDto>[] = [
    {
      field: 'timestamp',
      headerName: t('investigation.traces.timestamp'),
      width: 200,
      valueFormatter: (value: string) => new Date(value).toLocaleString(),
    },
    {
      field: 'type',
      headerName: t('investigation.traces.type'),
      width: 120,
      renderCell: (params) => {
        const value = params.value as string
        return <Chip label={value} size="small" color={value === 'Exception' ? 'error' : 'warning'} />
      },
    },
    {
      field: 'operationName',
      headerName: t('investigation.traces.operationName'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'resultCode',
      headerName: t('investigation.traces.resultCode'),
      width: 120,
    },
    {
      field: 'message',
      headerName: t('investigation.traces.message'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'appInsightsLink',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={t('investigation.traces.openInAppInsights')}>
          <IconButton size="small" href={params.value as string} target="_blank" rel="noopener">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box sx={{ height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 1, pb: 1 }}>
        <FormControlLabel
          control={<Switch checked={hideReadRequests} onChange={(_, checked) => setHideReadRequests(checked)} size="small" />}
          label={t('investigation.traces.hideReadRequests', 'Masquer les lectures')}
        />
      </Box>
      <DataGrid
        rows={filteredTraces}
        columns={columns}
        getRowId={(row) => `${row.timestamp}-${row.operationName}-${row.resultCode}`}
        density="compact"
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: 'timestamp', sort: 'desc' }] },
        }}
      />
    </Box>
  )
}

export default TracesPanel
